import React, { useEffect, useRef } from "react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";

const SECTION_LINKS = [
  { id: "overview", label: "Overview" },
  { id: "mechanics", label: "Mechanics" },
  { id: "flow", label: "How It Works" },
  { id: "use-cases", label: "Use Cases" },
];

const ConsumerPromo = () => {
  const overviewRef = useRef(null);
  const mechanicsRef = useRef(null);
  const flowRef = useRef(null);
  const useCasesRef = useRef(null);
  const ctaRef = useRef(null);

  const sectionRefs = {
    overview: overviewRef,
    mechanics: mechanicsRef,
    flow: flowRef,
    "use-cases": useCasesRef,
    cta: ctaRef,
  };

  const scrollToSection = (sectionId) => {
    sectionRefs[sectionId]?.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
          }
        });
      },
      { threshold: 0.12 },
    );

    const elements = document.querySelectorAll(".reveal");
    elements.forEach((el) => io.observe(el));

    return () => io.disconnect();
  }, []);
  return (
    <>
      <Navbar sectionLinks={SECTION_LINKS} onSectionSelect={scrollToSection} />

      <main id="display-ir">
        <section className="wrap hero-display section-loose reveal">
          <div>
            <div className="eyebrow">Consumer Promotions</div>
            <h1>Turn consumer promotions into measurable growth.</h1>
            <p>
              Deploy nationwide shopper promotions through messaging platforms, validate purchases
              through Receipt IR, and track campaign performance in real time — all within one
              unified platform.
            </p>
            <p>
              Kartini enables brands to activate consumer promotions through conversational
              journeys, digital vouchers, and structured validation workflows. Instead of fragmented
              campaign tools, Kartini connects shopper engagement, purchase verification, and
              analytics into one scalable environment.
            </p>
            <div className="hero-actions">
              <button className="btn dark" onClick={() => scrollToSection("cta")}>
                Launch a Promotion Demo
              </button>
              <button className="btn" onClick={() => scrollToSection("flow")}>
                See How It Works
              </button>
            </div>
          </div>
          <div className="hero-stage">
            <div className="device">
              <div className="device-top">
                <div className="device-title">
                  <div className="bubble">K</div>
                  <div>
                    <strong>Kartini Assistant</strong>
                    <br />
                    <span style={{ color: "#666", fontSize: "13px" }}>
                      Consumer Promotion Journey
                    </span>
                  </div>
                </div>
                <div className="mini-pill">Receipt IR Verified</div>
              </div>
              <div className="chat-flow">
                <div className="msg left">Welcome! Ready to claim today’s promotion?</div>
                <div className="msg right">Yes, show me the available offer.</div>
                <div className="msg left">
                  Buy participating products, upload your receipt, and receive your reward once
                  validated.
                </div>
                <div className="msg right">I’ve uploaded my receipt.</div>
                <div className="msg left">
                  Great. Your purchase has been validated. Your digital voucher is now ready.
                </div>
              </div>
            </div>
            <div className="floating float-a">
              <strong>Digital Voucher</strong>
              <span>Automated reward distribution after validation.</span>
            </div>
            <div className="floating float-b">
              <strong>Receipt IR</strong>
              <span>Authenticity, product match, quantity and retailer checks.</span>
            </div>
            <div className="floating float-c">
              <strong>Analytics</strong>
              <span>Track engagement, validation rate and reward issuance in real time.</span>
            </div>
          </div>
        </section>

        <section className="wrap reveal" id="overview" ref={overviewRef}>
          <div className="head">
            <div>
              <div className="eyebrow">The Problem</div>
              <h2>Consumer promotions are still operationally fragmented.</h2>
            </div>
            <p>
              Despite significant budget allocation, many consumer campaigns still depend on
              disconnected tools, manual validation, and incomplete reporting. The result is
              friction for shoppers and weak visibility for brands.
            </p>
          </div>
          <div className="grid4">
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">Challenge</div>
                <div className="metric-value">Coupon abuse and fraud</div>
              </div>
              <p>
                Unstructured validation workflows make campaigns vulnerable to misuse and duplicate
                claims.
              </p>
            </div>
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">Challenge</div>
                <div className="metric-value">Weak conversion visibility</div>
              </div>
              <p>
                Many campaigns show redemptions but fail to reveal the actual shopper journey behind
                them.
              </p>
            </div>
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">Challenge</div>
                <div className="metric-value">Slow deployment cycles</div>
              </div>
              <p>
                Operational complexity delays activation across channels, products and retail
                partners.
              </p>
            </div>
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">Challenge</div>
                <div className="metric-value">Poor retail linkage</div>
              </div>
              <p>
                Brands struggle to connect shopper engagement with real in-store purchase and
                retailer participation.
              </p>
            </div>
          </div>
        </section>

        <section className="wrap section-loose reveal">
          <div className="head">
            <div>
              <div className="eyebrow">What Kartini Enables</div>
              <h2>A unified consumer promotion infrastructure.</h2>
            </div>
            <p>
              Kartini gives brands one environment to manage promotion mechanics, shopper journeys,
              purchase validation, digital rewards and reporting without relying on fragmented
              campaign tools.
            </p>
          </div>
          <div className="grid4">
            <div className="panel feature">
              <div className="icon">SE</div>
              <div>
                <h3>Shopper Engagement</h3>
                <p>
                  Acquire and guide shoppers through promotional journeys using messaging platforms
                  they already use daily.
                </p>
              </div>
              <div className="placeholder">Messaging journey preview</div>
            </div>
            <div className="panel feature">
              <div className="icon">PM</div>
              <div>
                <h3>Promotion Mechanics</h3>
                <p>
                  Configure campaign rules, eligible products, purchase thresholds, rewards and
                  activation periods.
                </p>
              </div>
              <div className="placeholder">Campaign rule builder</div>
            </div>
            <div className="panel feature">
              <div className="icon">RV</div>
              <div>
                <h3>Purchase Verification</h3>
                <p>
                  Validate shopper purchases through Receipt IR to ensure authenticity and campaign
                  eligibility.
                </p>
              </div>
              <div className="placeholder">Receipt validation result</div>
            </div>
            <div className="panel feature">
              <div className="icon">AN</div>
              <div>
                <h3>Analytics</h3>
                <p>
                  Track shopper participation, reward issuance, campaign conversion and performance
                  trends in real time.
                </p>
              </div>
              <div className="placeholder">Promotion analytics dashboard</div>
            </div>
          </div>
        </section>

        <section className="wrap reveal" id="mechanics" ref={mechanicsRef}>
          <div className="head">
            <div>
              <div className="eyebrow">Key Promotion Mechanics</div>
              <h2>Built to support multiple consumer promotion formats.</h2>
            </div>
            <p>
              Kartini is flexible enough to support different shopper mechanics while keeping
              campaign execution structured, scalable and measurable for brand teams.
            </p>
          </div>
          <div className="grid2">
            <div className="panel">
              <h3>Digital Voucher Distribution</h3>
              <p>
                Shoppers receive redeemable vouchers after completing promotional requirements such
                as product purchase, journey completion, or campaign qualification.
              </p>
            </div>
            <div className="panel">
              <h3>Gift With Purchase</h3>
              <p>
                Shoppers upload receipts to claim physical or digital rewards through a guided
                validation and fulfillment flow.
              </p>
            </div>
            <div className="panel">
              <h3>Receipt-Based Cashback</h3>
              <p>
                Enable cashback or rebate campaigns where validated purchases trigger automated
                reward issuance.
              </p>
            </div>
            <div className="panel">
              <h3>Loyalty and Points Programs</h3>
              <p>
                Drive repeat engagement by allowing shoppers to accumulate and redeem points across
                multiple purchases.
              </p>
            </div>
          </div>
        </section>

        <section className="wrap section-loose reveal" id="flow" ref={flowRef}>
          <div className="head">
            <div>
              <div className="eyebrow">How It Works</div>
              <h2>A shopper flow built for engagement, validation and measurable outcomes.</h2>
            </div>
            <p>
              From entry to reward distribution, Kartini structures the full promotion journey so
              brands can engage shoppers without losing operational control or data visibility.
            </p>
          </div>
          <div className="steps">
            <div className="step">
              <div className="step-num">1</div>
              <h3>Campaign Entry</h3>
              <p>
                Shoppers discover promotions through QR codes, social campaigns, ads or product
                packaging and enter via a messaging chatbot.
              </p>
            </div>
            <div className="step">
              <div className="step-num">2</div>
              <h3>Shopper Engagement</h3>
              <p>
                The chatbot guides the shopper through product education, eligibility confirmation,
                store selection and promotion explanation.
              </p>
            </div>
            <div className="step">
              <div className="step-num">3</div>
              <h3>Purchase Validation</h3>
              <p>
                Receipt IR validates purchased products, quantities, retailer details and
                authenticity to ensure campaign compliance.
              </p>
            </div>
            <div className="step">
              <div className="step-num">4</div>
              <h3>Reward Distribution</h3>
              <p>
                Once validated, shoppers receive digital vouchers, cashback, loyalty points or gift
                redemption instructions.
              </p>
            </div>
            <div className="step">
              <div className="step-num">5</div>
              <h3>Analytics & Reporting</h3>
              <p>
                Brands gain real-time visibility into engagement, redemption, campaign ROI and
                retail participation.
              </p>
            </div>
          </div>
        </section>

        <section className="wrap reveal">
          <div className="head">
            <div>
              <div className="eyebrow">Why Messaging Works</div>
              <h2>Promotions should happen where shoppers already are.</h2>
            </div>
            <p>
              Traditional promotions often require downloads, websites and long forms.
              Messaging-first journeys remove that friction and create a simpler path from
              engagement to validation and reward.
            </p>
          </div>
          <div className="channel-tiles">
            <div className="tile">
              <h3>WhatsApp</h3>
              <p>
                Highly familiar, high-visibility and well-suited for conversational promotions with
                interactive buttons, lists and structured shopper flows.
              </p>
            </div>
            <div className="tile">
              <h3>Messenger</h3>
              <p>
                Strong fit for social traffic conversion, especially when campaigns originate from
                Facebook and Instagram ecosystems.
              </p>
            </div>
            <div className="tile">
              <h3>Viber</h3>
              <p>
                Useful in markets where Viber remains a strong consumer channel, especially for
                localized engagement and loyalty-oriented mechanics.
              </p>
            </div>
          </div>
        </section>

        <section className="wrap section-loose reveal">
          <div className="head">
            <div>
              <div className="eyebrow">Built for FMCG Scale</div>
              <h2>Campaign infrastructure designed for real market complexity.</h2>
            </div>
            <p>
              Kartini supports large-scale promotion programs across products, channels and retail
              environments, making it easier to execute campaigns nationally while keeping shopper
              engagement and reporting structured.
            </p>
          </div>
          <div className="grid3">
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">Scale</div>
                <div className="metric-value">Nationwide deployment</div>
              </div>
              <p>
                Launch programs across multiple channels, stores and markets with greater
                operational consistency.
              </p>
            </div>
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">Control</div>
                <div className="metric-value">Structured validation</div>
              </div>
              <p>
                Protect campaign quality through receipt verification, reward logic and defined
                shopper flows.
              </p>
            </div>
            <div className="panel metric-card">
              <div>
                <div className="metric-kicker">Visibility</div>
                <div className="metric-value">Real-time performance</div>
              </div>
              <p>
                Monitor shopper behavior, validation outcomes and campaign effectiveness as the
                program runs.
              </p>
            </div>
          </div>
        </section>

        <section className="wrap reveal" id="use-cases" ref={useCasesRef}>
          <div className="head">
            <div>
              <div className="eyebrow">Use Cases</div>
              <h2>Consumer promotion formats that support real brand objectives.</h2>
            </div>
            <p>
              Kartini is built for use cases that go beyond awareness — helping brands connect
              shopper engagement with measurable retail and purchase outcomes.
            </p>
          </div>
          <div className="grid2">
            <div className="panel">
              <h3>Product Launch Campaigns</h3>
              <p>
                Drive awareness and shopper trial for newly launched products through guided
                promotional journeys.
              </p>
            </div>
            <div className="panel">
              <h3>Category Growth Programs</h3>
              <p>
                Encourage shoppers to purchase more products within a category through incentives
                and bundled mechanics.
              </p>
            </div>
            <div className="panel">
              <h3>Retail-Specific Promotions</h3>
              <p>
                Run targeted campaigns with selected retailer partners while maintaining clearer
                visibility into participation.
              </p>
            </div>
            <div className="panel">
              <h3>Seasonal Campaigns</h3>
              <p>
                Activate promotional programs during festive seasons and peak demand periods with
                faster campaign readiness.
              </p>
            </div>
          </div>
        </section>

        <section className="wrap section-loose reveal">
          <div className="head">
            <div>
              <div className="eyebrow">Connected Platform</div>
              <h2>
                Consumer Promotions work stronger when connected to the wider Kartini ecosystem.
              </h2>
            </div>
            <p>
              Kartini consumer promotions become more powerful when combined with Display IR, Retail
              Execution and Analytics — allowing brands to connect shopper activation with in-store
              visibility and campaign intelligence.
            </p>
          </div>
          <div className="grid3">
            <div className="panel">
              <h3>Display IR</h3>
              <p>Verify shelf visibility and display execution during consumer campaigns.</p>
            </div>
            <div className="panel">
              <h3>Retail Execution</h3>
              <p>Track store participation and campaign readiness across the retail environment.</p>
            </div>
            <div className="panel">
              <h3>Analytics</h3>
              <p>
                Measure performance across shopper engagement, validation and reward issuance with
                one reporting layer.
              </p>
            </div>
          </div>
        </section>

        <section className="wrap reveal" id="cta" ref={ctaRef}>
          <div className="cta-display">
            <div className="eyebrow">Get Started</div>
            <h2>Build consumer promotions that deliver real insights.</h2>
            <p>
              Modern promotions should not only attract shoppers — they should generate structured
              intelligence about consumer behavior and retail performance. Kartini enables brands to
              deploy consumer promotions that are measurable, verifiable and scalable across modern
              retail environments.
            </p>
            <div className="hero-actions" style={{ justifyContent: "center" }}>
              <button className="btn dark">Book a Consumer Promotion Demo</button>
              <button className="btn">Explore the Kartini Platform</button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default ConsumerPromo;
